import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { User } from 'lucide-react';
import MobileHomeIcon from '@/assets/icons/probability-bottom-navigation/mobile-home.svg?react';
import MobileHomeActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-home-active.svg?react';
import MobileLeaderboardIcon from '@/assets/icons/probability-bottom-navigation/mobile-leaderboard.svg?react';
import MobileLeaderboardActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-leaderboard-active.svg?react';
import MobileFriendsIcon from '@/assets/icons/probability-bottom-navigation/mobile-friends.svg?react';
import MobileFriendsActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-friends-active.svg?react';
import { UserRole } from '@sportspredict/shared';

import { ROUTES } from '@/constants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/utils';
import { useDispatch } from 'react-redux';
import { openAuthModal } from '@/store/slices/auth-modal.slice';

import {
	getProbabilityEventIdFromPath,
	getProbabilityEventTabPath,
	getUserInitial,
	PROBABILITY_EVENT_TABS,
	type ProbabilityEventTab,
} from '../../utils/probability-event.utils';
import type { ProbabilityUser } from '../../types/probability-event.types';
import { useAuthFlow } from '@/hooks';

const NAV_ITEMS = [
	{
		icon: MobileHomeIcon,
		activeIcon: MobileHomeActiveIcon,
		tab: PROBABILITY_EVENT_TABS.HOME,
		size: 'h-[22px] w-[22px]',
		label: 'Home',
	},
	{
		icon: MobileLeaderboardIcon,
		activeIcon: MobileLeaderboardActiveIcon,
		tab: PROBABILITY_EVENT_TABS.LEADERBOARD,
		size: 'h-[22px] w-[22px]',
		label: 'Leaderboard',
	},
	{
		icon: MobileFriendsIcon,
		activeIcon: MobileFriendsActiveIcon,
		tab: PROBABILITY_EVENT_TABS.FRIENDS,
		size: 'h-6 w-6',
		label: 'Following',
	},
	{ icon: User, tab: PROBABILITY_EVENT_TABS.PROFILE, size: 'h-6 w-6', label: 'Profile' },
];

interface Props {
	user?: ProbabilityUser;
	authModalOpen: boolean;
	isPreStart?: boolean;
}

export function ProbabilityEventBottomNav({ user, authModalOpen, isPreStart = false }: Props) {
	const { pathname } = useLocation();
	const dispatch = useDispatch();
	const pointerActivatedRef = useRef(false);
	const { isWTN } = useAuthFlow();

	const eventId = getProbabilityEventIdFromPath(pathname);

	if (authModalOpen) return null;

	const roleNavItems =
		user?.role === UserRole.Admin
			? NAV_ITEMS.filter(
					({ tab }) =>
						tab !== PROBABILITY_EVENT_TABS.PROFILE && tab !== PROBABILITY_EVENT_TABS.FRIENDS
				)
			: NAV_ITEMS;
	const navItems = isPreStart
		? roleNavItems.filter(
				({ tab }) => tab === PROBABILITY_EVENT_TABS.HOME || tab === PROBABILITY_EVENT_TABS.PROFILE
			)
		: roleNavItems;

	const openAuth = () => {
		dispatch(openAuthModal());
		return true;
	};

	const handlePointerDown = (
		e: React.PointerEvent<HTMLAnchorElement>,
		tab: ProbabilityEventTab
	) => {
		if (!e.isPrimary) return;
		if (tab !== PROBABILITY_EVENT_TABS.PROFILE || user) return;

		pointerActivatedRef.current = true;
		e.preventDefault();
		openAuth();
	};

	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, tab: ProbabilityEventTab) => {
		if (tab === PROBABILITY_EVENT_TABS.HOME || user) {
			return;
		}

		e.preventDefault();

		if (pointerActivatedRef.current) {
			pointerActivatedRef.current = false;
			return;
		}

		openAuth();
	};

	return (
		<nav
			className={cn(
				'fixed bottom-0 left-0 right-0 z-50 border-t border-[#DCE3F9] bg-white',
				isWTN ? 'h-[90px]' : 'h-[72px]'
			)}
		>
			<div className="mx-auto flex h-[56px] max-w-[393px] items-start md:max-w-3xl">
				{navItems.map(({ icon: Icon, activeIcon: ActiveIcon, tab, size, label }) => {
					const to = eventId ? getProbabilityEventTabPath(eventId, tab) : ROUTES.HOME;

					const isActive =
						pathname === to ||
						pathname.endsWith(`/${tab}`) ||
						(tab === PROBABILITY_EVENT_TABS.HOME &&
							(pathname.endsWith(`/${PROBABILITY_EVENT_TABS.MARKETS}`) ||
								pathname.endsWith('/locked-in'))) ||
						(tab === PROBABILITY_EVENT_TABS.PROFILE && pathname.includes('/history/'));

					return (
						<Link
							key={tab}
							to={to}
							prefetch="viewport"
							aria-label={label}
							onClick={(e) => handleClick(e, tab)}
							onPointerDown={(e) => handlePointerDown(e, tab)}
							className="flex h-14 flex-1 flex-col items-center justify-start pt-3"
						>
							{tab === PROBABILITY_EVENT_TABS.PROFILE ? (
								<Avatar
									className={cn(
										'h-7 w-7 border',
										isActive && '-translate-y-[5px]',
										isActive ? 'border-[#2667FF]' : 'border-[#D9E2F0]'
									)}
								>
									<AvatarImage
										src={user?.img_variants?.xs || user?.img_variants?.s || undefined}
										alt={user?.full_name || 'Profile'}
									/>
									<AvatarFallback
										className={cn(
											"font-['DM_Sans'] text-[10px] font-bold",
											isActive ? 'bg-[#2667FF] text-white' : 'bg-[#D9E2F0] text-[#60758D]'
										)}
									>
										{getUserInitial(user)}
									</AvatarFallback>
								</Avatar>
							) : (
								<div className="flex flex-col items-center">
									<div
										className={cn(
											'flex h-6 w-6 shrink-0 items-center justify-center',
											isActive && '-translate-y-0.5'
										)}
									>
										{isActive && ActiveIcon ? (
											<ActiveIcon className={cn(size, 'block shrink-0')} />
										) : (
											<Icon className={cn(size, 'block shrink-0')} />
										)}
									</div>
									<span
										className={cn(
											'mt-1 h-0.5 w-1 shrink-0 rounded-full bg-[#2667FF]',
											!isActive && 'opacity-0'
										)}
									/>
								</div>
							)}

							{tab === PROBABILITY_EVENT_TABS.PROFILE && isActive && (
								<span className="mt-0 h-0.5 w-1 shrink-0 rounded-full bg-[#2667FF]" />
							)}
						</Link>
					);
				})}
			</div>
		</nav>
	);
}
